import React, { createContext, useState, useContext, useEffect } from 'react';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebase/config';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                // User is signed in, get additional user data from Firestore
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    setUser({ uid: user.uid, ...userDoc.data() });
                } else {
                    // User document doesn't exist, create one
                    const userData = {
                        email: user.email,
                        name: user.displayName || '',
                        membershipDate: new Date().toISOString(),
                        totalContributions: 0,
                        availableBalance: 0,
                        role: 'member',
                        status: 'active',
                        beneficiaries: [],
                        idDocuments: {
                            primaryId: 'pending',
                            secondaryId: 'pending'
                        }
                    };
                    await setDoc(doc(db, 'users', user.uid), userData);
                    setUser({ uid: user.uid, ...userData });
                }
            } else {
                setUser(null);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const login = async (email, password) => {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));

            if (userDoc.exists()) {
                return { success: true, user: { uid: userCredential.user.uid, ...userDoc.data() } };
            } else {
                return { success: false, error: 'User data not found' };
            }
        } catch (error) {
            let errorMessage = 'Login failed. Please try again.';
            switch (error.code) {
                case 'auth/user-not-found':
                    errorMessage = 'No user found with this email.';
                    break;
                case 'auth/wrong-password':
                    errorMessage = 'Incorrect password.';
                    break;
                case 'auth/invalid-email':
                    errorMessage = 'Invalid email address.';
                    break;
                default:
                    errorMessage = error.message;
            }
            return { success: false, error: errorMessage };
        }
    };

    const register = async (userData) => {
        try {
            // Create user in Firebase Auth
            const userCredential = await createUserWithEmailAndPassword(
                auth,
                userData.email,
                userData.password
            );

            const user = userCredential.user;

            // Update profile with display name
            await updateProfile(user, {
                displayName: `${userData.firstName} ${userData.lastName}`
            });

            // Create user document in Firestore with minimal data
            const userDoc = {
                email: userData.email,
                name: `${userData.firstName} ${userData.lastName}`,
                dateOfBirth: userData.dateOfBirth,
                membershipDate: new Date().toISOString(),
                totalContributions: 0,
                availableBalance: 0,
                role: 'member',
                status: 'active',
                beneficiaries: [],
                idDocuments: {
                    primaryId: 'pending',
                    secondaryId: 'pending'
                },
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            await setDoc(doc(db, 'users', user.uid), userDoc);

            return {
                success: true,
                user: { uid: user.uid, ...userDoc }
            };
        } catch (error) {
            let errorMessage = 'Registration failed. Please try again.';
            switch (error.code) {
                case 'auth/email-already-in-use':
                    errorMessage = 'Email already in use.';
                    break;
                case 'auth/weak-password':
                    errorMessage = 'Password is too weak.';
                    break;
                case 'auth/invalid-email':
                    errorMessage = 'Invalid email address.';
                    break;
                default:
                    errorMessage = error.message;
            }
            return { success: false, error: errorMessage };
        }
    };

    const uploadFile = async (file, path) => {
        try {
            const storageRef = ref(storage, `${path}/${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            console.error('Error uploading file:', error);
            throw error;
        }
    };

    const logout = async () => {
        try {
            await signOut(auth);
            setUser(null);
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const updateUserProfile = async (userId, updates) => {
        try {
            await updateDoc(doc(db, 'users', userId), {
                ...updates,
                updatedAt: new Date().toISOString()
            });

            // Update local state
            setUser(prev => ({ ...prev, ...updates }));
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    };

    const value = {
        user,
        login,
        register,
        logout,
        updateUserProfile,
        loading
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};